import React from 'react';

import {
    DATING_EVENTBRITE_SUB_CATEGORY,
    EVENTBRITE_CATEGORY_MAPPING,
    EVENTBRITE_CATEGORY_TO_REFINEMENT,
    HALLOWEEN_EVENTBRITE_SUB_CATEGORY,
    HOME_LIFESTYLE_EVENTBRITE_CATEGORY,
} from '@eventbrite/consumer-search.categories/constants';
import { MessageDescriptor, useIntl } from 'react-intl';

import { useAwsRum } from '@/common';
import { useEnvContext } from '@/shared/context/env';

import { IconCategoryCard } from '../IconCategoryCard';
import { CATEGORY_BROWSE_KEYS, EVENTBRITE_CATEGORY_CAROUSEL_ICON_MAP } from '../IconCategoryMap';
import { IconCategoryWrapper } from '../IconCategoryWrapper';

import styles from './IconCategoryBrowse.module.scss';

export interface IconCategory {
    key: string;
    name: MessageDescriptor;
    icon: React.ReactNode;
    link: string;
}

const getCategoryLink = (
    category: string,
    uses_language_subdirectory: boolean | undefined,
    locale: string | undefined,
) => {
    if (uses_language_subdirectory) {
        if (category === HALLOWEEN_EVENTBRITE_SUB_CATEGORY) {
            return `/${locale}/d/local/`;
        }
        return category == DATING_EVENTBRITE_SUB_CATEGORY
            ? `/${locale}/b/local/${EVENTBRITE_CATEGORY_TO_REFINEMENT[HOME_LIFESTYLE_EVENTBRITE_CATEGORY]}/`
            : `/${locale}/b/local/`;
    }
    if (category === HALLOWEEN_EVENTBRITE_SUB_CATEGORY) {
        return `/d/local/`;
    }
    return category == DATING_EVENTBRITE_SUB_CATEGORY
        ? `/b/local/${EVENTBRITE_CATEGORY_TO_REFINEMENT[HOME_LIFESTYLE_EVENTBRITE_CATEGORY]}/`
        : `/b/local/`;
};

const getCategories = (
    uses_language_subdirectory: boolean | undefined,
    locale: string | undefined,
) =>
    CATEGORY_BROWSE_KEYS.map((category) => {
        const categoryKey = EVENTBRITE_CATEGORY_TO_REFINEMENT[category];
        return {
            key: categoryKey,
            name: EVENTBRITE_CATEGORY_MAPPING[category],
            icon: EVENTBRITE_CATEGORY_CAROUSEL_ICON_MAP[category],
            link: getCategoryLink(category, uses_language_subdirectory, locale) + categoryKey,
        };
    });

export const IconCategoryBrowse = () => {
    const { env } = useEnvContext();
    const awsRum = useAwsRum();
    const intl = useIntl();
    const { uses_language_subdirectory, locale } = env?.localeInfo || {};
    const categories = getCategories(uses_language_subdirectory, locale);

    const onCategoryClick = (category: IconCategory) => {
        if (awsRum) {
            awsRum.recordEvent('Home-Category-Click-Icon', {
                categoryKey: category.key,
                categoryName: intl.formatMessage(category.name),
            });
        }
    };

    return (
        <section className={styles.root} data-testid="icon-category-browse">
            <IconCategoryWrapper cards={categories}>
                {(category) => {
                    return (
                        <a
                            key={category.key}
                            href={category.link}
                            data-testid="category-card"
                            data-heap-id="homepage-webapp-category-tiles"
                            className={styles.link}
                            onClick={() => onCategoryClick(category)}
                        >
                            <IconCategoryCard
                                titleMessage={category.name}
                                icon={category.icon}
                                showNewBadge={false}
                            />
                        </a>
                    );
                }}
            </IconCategoryWrapper>
        </section>
    );
};
